<script setup lang="ts">
const props = defineProps<{
  variant: SingleProductVariant | PacketVariant
  dir: 'horizontal' | 'vertical'
}>()

const totStudents = useTotStudents()

const min = computed(() => (props.variant.fixedAmount ? totStudents.value : 1))

const quantity = ref(min.value)

function handleChange(event: Event) {
  const value = Number((event.target as HTMLInputElement).value)
  if (value < min.value) {
    quantity.value = min.value
  }
}

const auth = useAuthorizeStore()

const prices = usePrices(props.variant)

const sum = computed(() => {
  if (prices.value === 'NOT_APPLICABLE') {
    return {
      exVat: 0,
      incVat: 0,
    }
  }

  return {
    exVat: quantity.value * prices.value.actual.exVat,
    incVat: quantity.value * prices.value.actual.incVat,
  }
})

const hasVat = computed(() => sum.value.incVat !== sum.value.exVat)
</script>

<template>
  <div v-if="prices === 'NOT_APPLICABLE'" class="card @container">
    <CardsCompanyPriceDisclaimer class="@xs:flex-row @xs:py-legacy-l @xs:text-start" />
  </div>
  <div v-else class="card">
    <div
      v-if="!isConsumer(auth.user)"
      class="price"
      :class="dir"
    >
      <IconsDiscount
        v-if="prices.discounted && prices.discounted.percentage < 100"
        :value="prices.discounted.percentage"
        size="small"
        class="[--background:var(--color-coral-pop)] [--color:white] [--stroke:white]"
      />
      <div>
        <div class="price">
          <span v-if="prices.discounted && prices.discounted.percentage === 100" class="text-xl font-semibold text-coral-legacy-40">
            Gratis*
          </span>
          <template v-else>
            <del
              v-if="prices.discounted"
              v-text="norwegianPrice(prices.original.exVat)"
            />
            <strong>
              {{ norwegianPrice(prices.actual.exVat) }} per tilgang per år
            </strong>
          </template>
          <span v-if="dir === 'horizontal' && hasVat">
            ({{ norwegianPrice(prices.actual.incVat) }} inkl. mva.)
          </span>
        </div>
        <div v-if="dir === 'vertical' && hasVat">
          {{ norwegianPrice(prices.actual.incVat) }} inkl. mva.
        </div>
        <DiscountDisclaimer
          :prices="prices"
          class="text-xs"
          fallback="Tilbudet gjelder første år. Deretter gjelder ordinær pris."
        />
      </div>
    </div>

    <div
      class="form"
      :class="[dir, { consumer: isConsumer(auth.user) }]"
    >
      <template v-if="isConsumer(auth.user)">
        <span>
          <del
            v-if="prices?.discounted"
            v-text="norwegianPrice(prices.original.incVat)"
          />
          <strong>
            {{ norwegianPrice(prices?.actual.incVat) }} per tilgang per år
          </strong>
        </span>

        <AddToCartButton
          :variant="variant"
          :quantity="quantity"
          class="flex-1 self-center text-black"
          :class="{
            'mt-3 ml-auto': dir === 'horizontal',
            'self-stretch': dir === 'vertical',
          }"
        />
      </template>
      <template v-else>
        <div class="quantities">
          <label for="quantity">Tilganger</label>
          <UInput
            v-model.number="quantity"
            variant="outline"
            name="quantity"
            type="number"
            :min="min"
            :ui="{
              base: 'ring-gray-30 rounded-none w-[10ch] text-base px-5 py-legacy-xs',
            }"
            :disabled="variant.fixedAmount"
            @focus="$event.target.select()"
            @change="handleChange"
          />
        </div>

        <div class="sum" :class="dir">
          <label for="sum">Sum&nbsp;</label>
          <output
            id="sum"
            for="quantity"
            v-text="norwegianPrice(sum.incVat)"
          />
          <span> inkl.&nbsp;mva.</span>
        </div>

        <AddToCartButton
          :variant="variant"
          :quantity="quantity"
          class="flex-1 self-center text-black"
          :class="{
            'mt-3 ml-auto': dir === 'horizontal',
            'self-stretch': dir === 'vertical',
          }"
        >
          Legg i handlekurv
        </AddToCartButton>
      </template>
    </div>

    <PriceLoginCta
      class="text-xs"
      :priceable="variant"
      singular-price
      :class="{ 'text-center': dir === 'vertical' }"
    />
  </div>
</template>

<style scoped>
@reference "~/assets/styles/main.css";

.card {
  background: white;
  padding: var(--spacing-legacy-m);
  border-radius: var(--radius-md);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-legacy-s);
  container-type: inline-size;
}

.price {
  @apply text-sm-loose flex items-center gap-s;

  &.vertical {
    flex-direction: column;
    text-align: center;

    > div {
      margin-inline: var(--spacing-legacy-l);

      > .price {
        justify-content: center;
      }
    }
  }

  > div {
    > .price {
      gap: var(--spacing-legacy-2xs);
      flex-wrap: wrap;
    }
  }
}

.form {
  background: var(--color-gray-5);
  display: flex;
  align-items: center;
  padding-block: var(--padding-block);
  padding-inline: var(--padding-inline);
  border-radius: var(--radius-sm);
  column-gap: min(var(--spacing-legacy-s), 5%);
  row-gap: 0.25rem;

  &.consumer {
    justify-content: space-between;
    padding: var(--spacing-legacy-m);

    > span {
      @apply flex gap-2 text-sm-loose;
    }
  }

  &.vertical {
    flex-direction: column;

    > .quantities > * {
      text-align: center;
    }
  }

  &.horizontal {
    flex-wrap: wrap;
  }

  > .quantities {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    > label {
      font-size: 0.75rem;
    }
  }
}

.sum {
  @apply text-base;
  flex: 1 auto;
  font-weight: 600;

  > span {
    font-weight: normal;
  }

  &.vertical {
    margin-top: var(--spacing-legacy-m);
    margin-bottom: var(--spacing-legacy-2xs);
  }

  &.horizontal {
    width: 35%;
    margin-top: 1.25rem;
  }
}
</style>
